import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { SparkInput } from '../components/SparkInput';
import { AuthenticatorSetup } from '../components/mfa/AuthenticatorSetup';
import { BackupCodes } from '../components/mfa/BackupCodes';
import { SparkMessage } from '../components/SparkMessage';
import { useAuth } from '../context/AuthContext';
import {
  ApiError,
  disableMfa,
  enableMfa,
  fetchMfaStatus,
  newRecoveryCodes,
  startMfaSetup,
} from '../lib/api';
import type { MfaSetup, MfaStatus } from '../lib/types';

/**
 * Two-step verification with Google Authenticator (or any TOTP app): turn it
 * on by scanning a QR code, get backup codes, or turn it off again.
 */
export function SecurityPage() {
  const { user, refreshUser } = useAuth();

  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  // Which form is open: none, the password prompt that starts setup, the QR
  // step, the turn-off form, or the new-backup-codes form.
  const [mode, setMode] = useState<'idle' | 'password' | 'scan' | 'disable' | 'regenerate'>('idle');
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await fetchMfaStatus());
      setLoadError('');
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load your security settings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = (next: typeof mode) => {
    setMode(next);
    setPassword('');
    setCode('');
    setFieldErrors({});
    setFormError('');
  };

  const fail = (err: unknown, fallback: string) => {
    if (err instanceof ApiError) {
      setFormError(err.message);
      setFieldErrors(err.fields ?? {});
    } else {
      setFormError(fallback);
    }
  };

  const handleStart = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password) {
      setFieldErrors({ password: 'Enter your password.' });
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      setSetup(await startMfaSetup(password));
      resetForm('scan');
    } catch (err) {
      fail(err, 'Setup could not be started. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnable = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (code.length !== 6) {
      setFieldErrors({ code: 'Enter the 6-digit code from the app.' });
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const result = await enableMfa(code);
      setRecoveryCodes(result.recoveryCodes);
      setSetup(null);
      resetForm('idle');
      setNotice('Two-step verification is on. You will be asked for a code each time you sign in.');
      await Promise.all([load(), refreshUser()]);
    } catch (err) {
      fail(err, 'The code could not be checked. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors: Record<string, string> = {};
    if (!password) errors.password = 'Enter your password.';
    if (!code.trim()) errors.code = 'Enter a code from the app or a backup code.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSubmitting(true);
    setFormError('');
    try {
      await disableMfa(password, code.trim());
      setRecoveryCodes(null);
      resetForm('idle');
      setNotice('Two-step verification is off. Your password alone now signs you in.');
      await Promise.all([load(), refreshUser()]);
    } catch (err) {
      fail(err, 'Two-step verification could not be turned off.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!code.trim()) {
      setFieldErrors({ code: 'Enter the 6-digit code from the app.' });
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const result = await newRecoveryCodes(code.trim());
      setRecoveryCodes(result.recoveryCodes);
      resetForm('idle');
      setNotice('New backup codes were made. The old ones no longer work.');
      await load();
    } catch (err) {
      fail(err, 'New backup codes could not be made.');
    } finally {
      setSubmitting(false);
    }
  };

  const codeInput = (label: string, allowBackup: boolean) => (
    <SparkInput
      label={label}
      inputMode={allowBackup ? 'text' : 'numeric'}
      autoComplete="one-time-code"
      maxLength={allowBackup ? 16 : 6}
      placeholder={allowBackup ? '123456 or ABCDE-FGH23' : '123456'}
      value={code}
      error={fieldErrors.code}
      onChange={(event) =>
        setCode(allowBackup ? event.target.value.toUpperCase() : event.target.value.replace(/\D/g, ''))
      }
      disabled={submitting}
    />
  );

  const cancelButton = (
    <button
      type="button"
      className="spark-btn-link"
      onClick={() => {
        setSetup(null);
        resetForm('idle');
      }}
      disabled={submitting}
    >
      Cancel
    </button>
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Security</h1>
          <p className="page-header__subtitle">
            Protect your account with a code from Google Authenticator as well as your password.
          </p>
        </div>
      </div>

      {loadError && <SparkMessage variant="error">{loadError}</SparkMessage>}
      {notice && <SparkMessage variant="success">{notice}</SparkMessage>}

      {recoveryCodes && (
        <div className="card security-card">
          <div className="card__body">
            <h2 className="security-card__title">Save your backup codes</h2>
            <BackupCodes codes={recoveryCodes} userId={user?.userId}>
              <button type="button" className="spark-btn-link" onClick={() => setRecoveryCodes(null)}>
                I have saved them
              </button>
            </BackupCodes>
          </div>
        </div>
      )}

      <div className="card security-card">
        <div className="card__body">
          <h2 className="security-card__title">
            Two-step verification{' '}
            {status && (
              <span className={`badge badge--${status.enabled ? 'active' : 'disabled'}`}>
                {status.enabled ? 'On' : 'Off'}
              </span>
            )}
          </h2>

          {formError && <SparkMessage variant="error">{formError}</SparkMessage>}

          {status && !status.enabled && mode === 'idle' && (
            <>
              <p className="field-hint">
                When this is on, signing in asks for your password and then a 6-digit code from the Google
                Authenticator app on your phone. Microsoft Authenticator, Authy and 1Password work too.
              </p>
              <button type="button" className="spark-btn spark-btn--primary" onClick={() => resetForm('password')}>
                Turn on
              </button>
            </>
          )}

          {mode === 'password' && (
            <form onSubmit={handleStart} noValidate className="security-form">
              <p className="field-hint">Confirm your password to start.</p>
              <SparkInput
                label="Password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                error={fieldErrors.password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
              />
              <div className="security-actions">
                <button type="submit" className="spark-btn spark-btn--primary" disabled={submitting}>
                  {submitting ? 'Checking…' : 'Continue'}
                </button>
                {cancelButton}
              </div>
            </form>
          )}

          {mode === 'scan' && setup && (
            <form onSubmit={handleEnable} noValidate className="security-form">
              <AuthenticatorSetup setup={setup} />
              {codeInput('Authentication code', false)}
              <div className="security-actions">
                <button type="submit" className="spark-btn spark-btn--primary" disabled={submitting}>
                  {submitting ? 'Checking…' : 'Turn on'}
                </button>
                {cancelButton}
              </div>
            </form>
          )}

          {status?.enabled && mode === 'idle' && (
            <>
              <p className="field-hint">
                Signing in asks for a code from your authenticator app. You have{' '}
                <strong>{status.recoveryCodesLeft}</strong> unused backup code
                {status.recoveryCodesLeft === 1 ? '' : 's'} left.
              </p>
              <div className="security-actions">
                <button type="button" className="spark-btn spark-btn--secondary" onClick={() => resetForm('regenerate')}>
                  Get new backup codes
                </button>
                {!status.required && (
                  <button type="button" className="spark-btn spark-btn--secondary" onClick={() => resetForm('disable')}>
                    Turn off
                  </button>
                )}
              </div>
              {status.required && (
                <p className="field-hint">Your agency requires two-step verification, so it cannot be turned off.</p>
              )}
            </>
          )}

          {mode === 'regenerate' && (
            <form onSubmit={handleRegenerate} noValidate className="security-form">
              <p className="field-hint">Enter a code from your app. Your old backup codes will stop working.</p>
              {codeInput('Authentication code', false)}
              <div className="security-actions">
                <button type="submit" className="spark-btn spark-btn--primary" disabled={submitting}>
                  {submitting ? 'Working…' : 'Make new codes'}
                </button>
                {cancelButton}
              </div>
            </form>
          )}

          {mode === 'disable' && (
            <form onSubmit={handleDisable} noValidate className="security-form">
              <p className="field-hint">Confirm with your password and a code from the app (or a backup code).</p>
              <SparkInput
                label="Password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                error={fieldErrors.password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
              />
              {codeInput('Code', true)}
              <div className="security-actions">
                <button type="submit" className="spark-btn spark-btn--primary" disabled={submitting}>
                  {submitting ? 'Working…' : 'Turn off'}
                </button>
                {cancelButton}
              </div>
            </form>
          )}
        </div>
      </div>

      <p className="field-hint">
        Lost your phone and your backup codes? Ask your agency administrator to reset two-step verification.{' '}
        <Link className="spark-link" to="/overview">
          Back to overview
        </Link>
      </p>
    </>
  );
}
